import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import EntityTypeahead from "@/components/EntityTypeahead";
import { Play, Users, User, Building2, Trophy, Shield } from "lucide-react";
import YSVLogo from "@/components/YSVLogo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const roles = [
  { value: "player_parent", label: "Player / Parent", icon: User, desc: "Watch games, create clips & highlights", approvalNote: "Requires Org Admin approval after signup" },
  { value: "amateur", label: "Amateur (Adult Player)", icon: User, desc: "Adult amateur player — watch games, create clips", approvalNote: "Adult amateur league only — requires admin approval" },
  { value: "coach", label: "Coach", icon: Users, desc: "Review footage & manage your team", approvalNote: "Requires Org Admin approval after signup" },
  { value: "org_admin", label: "Organization Admin", icon: Building2, desc: "Manage teams & rosters", approvalNote: "Requires League Admin approval after signup" },
];

const GetStartedPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [selectedRole, setSelectedRole] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "", team_id: "", org_id: "", parent_email: "" });
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);

  // The league list used to be preloaded here only to name the amateur leagues
  // in helper text. The team picker now filters by league type server-side
  // (?league_type=), which is the same guard register applies, so neither the
  // fetch nor the client-side filter is needed.

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      const payload = {
        email: form.email, password: form.password, name: form.name, role: selectedRole,
      };
      if (selectedRole === "player_parent" || selectedRole === "coach" || selectedRole === "amateur") {
        // Org AND team are both mandatory (league is derived from them
        // server-side) — typing in a typeahead without picking an option
        // passes HTML `required`, so enforce the actual selection here.
        if (!form.org_id) {
          setError("Please select your organization.");
          setSending(false);
          return;
        }
        if (!form.team_id) {
          setError("Please select your team.");
          setSending(false);
          return;
        }
        payload.org_id = form.org_id;
        payload.team_id = form.team_id;
      }
      if (selectedRole === "player_parent") {
        const pe = (form.parent_email || "").trim();
        if (!pe || !pe.includes("@")) {
          setError("Parent's email is required for player/parent signup.");
          setSending(false);
          return;
        }
        payload.parent_email = pe;
      }
      if (selectedRole === "org_admin") {
        // Same rule as above: HTML `required` passes on typed-but-not-picked
        // text, so check the actual selection before hitting the server.
        if (!form.org_id) {
          setError("Please select your organization.");
          setSending(false);
          return;
        }
        payload.org_id = form.org_id;
      }
      await axios.post(`${API}/auth/register`, payload);
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Registration failed. Please try again.");
    }
    setSending(false);
  };

  return (
    <div data-testid="get-started-page" className="page-gradient" style={{
      paddingTop: 120, minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: '120px 24px 80px',
    }}>
      <div style={{ maxWidth: 520, width: '100%', position: 'relative', zIndex: 1 }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <Link to="/landing" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            textDecoration: 'none',
          }}>
            <YSVLogo size="lg" showSubtext />
          </Link>
          <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 28, fontWeight: 700, marginTop: 16, marginBottom: 8 }}>
            Get Started with YSV
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            {step === 1 ? 'Select your role to get started.' : 'Create your account.'}
          </p>
        </div>

        {success ? (
          <div data-testid="signup-success" className="glass-card animate-fade-in-up" style={{ padding: 40, textAlign: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(255, 152, 0, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
              <Shield size={28} style={{ color: '#ff9800' }} />
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Account Created!</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 8, lineHeight: 1.6 }}>
              Your account has been created successfully.
            </p>
            {selectedRole !== "ysv_admin" && (
              <p style={{ color: '#ff9800', fontSize: 13, marginBottom: 24, lineHeight: 1.6 }}>
                {selectedRole === "org_admin"
                  ? "Your account is pending approval from a League Administrator."
                  : "Your account is pending approval from your Organization Administrator."
                }
              </p>
            )}
            <Link to="/login" className="btn-primary" style={{ textDecoration: 'none', padding: '12px 28px', fontSize: 14 }}>
              Go to Sign In
            </Link>
          </div>
        ) : (
          <div className="glass-card" style={{ padding: 32 }}>
            {step === 1 && (
              <div data-testid="step-role-select" className="animate-fade-in">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {roles.map(role => (
                    <button key={role.value} data-testid={`role-${role.value}`}
                      onClick={() => { setSelectedRole(role.value); setStep(2); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 16, padding: '18px 20px',
                        background: 'var(--dark-card)', border: '1px solid var(--dark-border)',
                        borderRadius: 12, color: 'var(--text-primary)', cursor: 'pointer',
                        transition: 'all 0.2s ease', textAlign: 'left', width: '100%',
                        fontFamily: "'DM Sans', sans-serif",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--cyan)'; e.currentTarget.style.background = 'var(--dark-card-hover)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--dark-border)'; e.currentTarget.style.background = 'var(--dark-card)'; }}
                    >
                      <div className="icon-box icon-box-cyan"><role.icon size={20} /></div>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontSize: 15, fontWeight: 600 }}>{role.label}</p>
                        <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{role.desc}</p>
                        <p style={{ fontSize: 10, color: '#ff9800', marginTop: 4 }}>{role.approvalNote}</p>
                      </div>
                    </button>
                  ))}

                  {/* League / Org signup — routes to its own fillable form */}
                  <button
                    type="button"
                    data-testid="role-league-org-signup"
                    onClick={() => navigate("/league-signup")}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 16, padding: '18px 20px',
                      background: 'var(--dark-card)', border: '1px solid var(--dark-border)',
                      borderRadius: 12, color: 'var(--text-primary)', cursor: 'pointer',
                      transition: 'all 0.2s ease', textAlign: 'left', width: '100%',
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--cyan)'; e.currentTarget.style.background = 'var(--dark-card-hover)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--dark-border)'; e.currentTarget.style.background = 'var(--dark-card)'; }}
                  >
                    <div className="icon-box icon-box-cyan"><Trophy size={20} /></div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 15, fontWeight: 600 }}>Sign up a League / Organization</p>
                      <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>Bring your league or org onto YSV — we'll reach out personally</p>
                      <p style={{ fontSize: 10, color: 'var(--cyan)', marginTop: 4 }}>Quick application form · no password needed</p>
                    </div>
                  </button>
                </div>
                <div style={{ textAlign: 'center', marginTop: 24 }}>
                  <p style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    Already have an account? <Link to="/login" style={{ color: 'var(--cyan)', textDecoration: 'none', fontWeight: 600 }}>Sign in</Link>
                  </p>
                </div>
              </div>
            )}

            {step === 2 && (
              <form data-testid="signup-form" onSubmit={handleSubmit} className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <button type="button" data-testid="back-btn" onClick={() => setStep(1)} style={{
                  background: 'none', border: 'none', color: 'var(--text-secondary)',
                  cursor: 'pointer', fontSize: 13, textAlign: 'left', padding: 0, marginBottom: 4,
                  fontFamily: "'DM Sans', sans-serif",
                }}>&larr; Back to role selection</button>

                {error && <div data-testid="signup-error" style={{ background: 'rgba(244,67,54,0.1)', border: '1px solid rgba(244,67,54,0.3)', borderRadius: 8, padding: '10px 14px', color: '#f44336', fontSize: 13 }}>{error}</div>}

                <input className="input-field" data-testid="signup-name" placeholder="Your Full Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
                <input className="input-field" data-testid="signup-email" type="email" placeholder="Email Address" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required />
                <input className="input-field" data-testid="signup-password" type="password" placeholder="Password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required minLength={4} />

                {(selectedRole === "player_parent" || selectedRole === "coach" || selectedRole === "amateur") && (
                  <>
                    <div>
                      {selectedRole === "amateur" && (
                        <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8 }}>
                          Amateur accounts join adult amateur leagues, so only those teams are listed.
                        </p>
                      )}
                      <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Select your organization</label>
                      <EntityTypeahead
                        endpoint="/public/organizations"
                        value={form.org_id}
                        onChange={(id) => setForm({ ...form, org_id: id, team_id: "" })}
                        placeholder="Type your organization or club name…"
                        emptyLabel="No organizations match"
                        testid="signup-org"
                        required
                      />
                      <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                        Your league is set automatically from your organization.
                      </p>
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Select your team</label>
                      {/* Org first, then a team inside it (team names are often
                          just "9U", so an unscoped search was a guessing game).
                          Still server-side search, still scoped to the league
                          type this role may join so the picker can't offer a
                          team that register would then reject. */}
                      <EntityTypeahead
                        endpoint="/public/teams"
                        params={{ org_id: form.org_id || undefined, league_type: selectedRole === "amateur" ? "amateur" : "youth" }}
                        value={form.team_id}
                        onChange={(id) => setForm({ ...form, team_id: id })}
                        placeholder={form.org_id ? "Start typing your team name…" : "Select your organization first"}
                        emptyLabel={selectedRole === "amateur"
                          ? "No adult amateur teams match"
                          : "No teams match"}
                        disabled={!form.org_id}
                        testid="signup-team"
                        required
                      />
                    </div>
                  </>
                )}

                {selectedRole === "player_parent" && (
                  <div>
                    <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>
                      Parent's email <span style={{ color: '#ff9800' }}>(required)</span>
                    </label>
                    <input
                      className="input-field"
                      data-testid="signup-parent-email"
                      type="email"
                      placeholder="parent@example.com"
                      value={form.parent_email}
                      onChange={e => setForm({ ...form, parent_email: e.target.value })}
                      required
                    />
                    <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                      We'll email your parent a one-click link so they can claim a free YSV account and watch your games.
                    </p>
                  </div>
                )}

                {selectedRole === "org_admin" && (
                  <div>
                    <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Select organization</label>
                    {/* Was a plain <select> over a list the API capped at 100
                        rows alphabetically, so clubs later in the alphabet were
                        simply not offered ("not all the organizations are
                        showing up"). Searches server-side now, like the team
                        picker above. */}
                    <EntityTypeahead
                      endpoint="/public/organizations"
                      value={form.org_id}
                      onChange={(id) => setForm({ ...form, org_id: id })}
                      placeholder="Type your organization name…"
                      emptyLabel="No organizations match"
                      testid="signup-org"
                      required
                    />
                  </div>
                )}

                <div style={{ background: 'rgba(255, 152, 0, 0.08)', border: '1px solid rgba(255, 152, 0, 0.2)', borderRadius: 8, padding: '10px 14px', marginTop: 4 }}>
                  <p style={{ fontSize: 12, color: '#ff9800' }}>
                    {selectedRole === "org_admin"
                      ? "Your account will require approval from a League Administrator after signup before you can access the platform."
                      : "Your account will require approval from an Organization Administrator after signup before you can access team content."
                    }
                  </p>
                </div>

                <button type="submit" data-testid="signup-submit" className="btn-primary" disabled={sending} style={{ width: '100%', marginTop: 8, opacity: sending ? 0.7 : 1 }}>
                  {sending ? 'Creating Account...' : 'Create Account'}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GetStartedPage;
